import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { withSession } from '@/lib/sessionBridge';
import {
  shipToFromLookup,
  normalizeShipTo,
  shipToContactFields,
  classifyAddressSaveError,
} from '@/lib/followupShipTo';

// §patient-address state for the follow-up Ship-to card. Two paths:
//
//   'auth'    the dashboard handed us a session token (lib/sessionBridge). The
//             card shows GET /beluga/patient-address `current`; Save POSTs
//             /beluga/update-patient-address BEFORE payment, and the submit
//             carries no address (the BE's Tier-0 inheritance uses the saved one).
//   'contact' no token, or the routes 404. The card shows the lookup address; a
//             change goes out as contact.{address,city,state,zip} on submit.
//
// 'pending' until the first GET settles; a Save made meanwhile waits on the same
// session request, so it takes whichever path the token decides.
export default function useFollowupShipTo({ userData, previousMasterId, active }) {
  const lookupAddress = useMemo(() => shipToFromLookup(userData), [userData]);
  const [mode, setMode] = useState('pending');
  const [serverAddress, setServerAddress] = useState(null);
  const [changed, setChanged] = useState(null);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  // A Save that lands before the GET returns must not be overwritten by it.
  const savedRef = useRef(false);

  useEffect(() => {
    if (!active || !previousMasterId || mode !== 'pending') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await withSession((t) => apiService.getPatientAddress(previousMasterId, t));
        if (cancelled) return;
        if (r.unauthenticated) {
          setMode('contact');
          return;
        }
        const current = r.value && r.value.current;
        if (!savedRef.current) setServerAddress(current ? normalizeShipTo(current) : null);
        setMode('auth');
      } catch (e) {
        // 404 (routes unmounted / not this patient's visit) or unreachable.
        if (!cancelled) setMode('contact');
      }
    })();
    return () => { cancelled = true; };
  }, [active, previousMasterId, mode]);

  // ShipToCard's onChange. Resolves undefined on success, or
  // { fieldErrors } / { formError } to keep the form open.
  const save = useCallback(async (addr) => {
    if (!addr) {
      setChanged(null);
      return undefined;
    }
    const toContact = () => {
      setMode('contact');
      setChanged(addr);
    };
    if (mode === 'contact' || !previousMasterId) {
      toContact();
      return undefined;
    }
    try {
      const r = await withSession((t) =>
        apiService.updatePatientAddress({ masterId: previousMasterId, ...addr }, t),
      );
      if (r.unauthenticated) {
        toContact();
        return undefined;
      }
      savedRef.current = true;
      setServerAddress(normalizeShipTo((r.value && r.value.address) || addr));
      setChanged(null);
      setSaved(true);
      setMode('auth');
      return undefined;
    } catch (e) {
      const c = classifyAddressSaveError(e);
      if (c.kind === 'fallback') {
        toContact();
        return undefined;
      }
      return c.kind === 'field' ? { fieldErrors: { [c.field]: c.message } } : { formError: c.message };
    }
  }, [mode, previousMasterId]);

  // After a successful submit: the change now lives on the new visit.
  const afterSubmit = useCallback(() => {
    setChanged(null);
    setSaved(false);
  }, []);

  const reset = useCallback(() => {
    savedRef.current = false;
    setMode('pending');
    setServerAddress(null);
    setChanged(null);
    setSaved(false);
    setEditing(false);
  }, []);

  return {
    mode,
    onFile: mode === 'auth' ? serverAddress : lookupAddress,
    changed,
    saved,
    editing,
    setEditing,
    save,
    afterSubmit,
    reset,
    // Only the fallback path puts the address on the submit.
    contactFields: mode === 'contact' ? shipToContactFields(changed) : {},
  };
}
